/**
 * javaStructureParser.js
 *
 * Structure-aware Java code analysis for safe TODO stubbing.
 *
 * Developer note: This is a pragmatic heuristic parser, not a full AST.
 * It reliably handles the common patterns (method bodies, constructors).
 * Edge cases (lambdas, anonymous classes) fall through to the single-line
 * TODO fallback in handleMarkDeferred — which is always the safe outcome.
 *
 * All functions are pure: take string content, return results.
 * No Monaco imports.
 *
 * Disable in prod: the only side effect is console.debug calls (no-op in prod builds).
 */

const JAVA_ACCESS_MOD_RE = /\b(public|private|protected|static|final|synchronized|native|default|abstract)\b/
const JAVA_METHOD_HINT_RE = /\b(void|boolean|int|long|double|float|char|byte|short|String|List|Map|Set|Optional)\s+\w+\s*\(/

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Map a Java type name to its default return literal.
 * Returns null for void / constructors (no return statement needed).
 */
function defaultForType(returnType) {
  if (!returnType || /^void$/i.test(returnType.trim())) return null
  if (/^boolean$/i.test(returnType)) return 'false'
  if (/^(int|long|short|byte)$/.test(returnType)) return '0'
  if (/^(float|double)$/.test(returnType)) return '0.0'
  if (/^char$/.test(returnType)) return "'\\0'"
  return 'null' // String, Object, generics, arrays
}

/**
 * Extract the return type from the concatenated signature lines.
 * Strips access modifiers and annotations, takes the token immediately
 * before `methodName(`.
 */
function extractReturnType(sigLines) {
  const flat = sigLines.join(' ').replace(/\s+/g, ' ').trim()

  // Quick shortcut for void
  if (/\bvoid\b/.test(flat)) return 'void'

  // Primitives
  for (const p of ['boolean', 'int', 'long', 'double', 'float', 'char', 'byte', 'short']) {
    if (new RegExp(`\\b${p}\\b`).test(flat)) return p
  }

  // Strip annotations (@Foo, @Foo("bar")) and access modifiers to isolate return type
  const stripped = flat
    .replace(/@\w+(\([^)]*\))?\s*/g, '')
    .replace(/\b(public|private|protected|static|final|synchronized|native|abstract|default|override)\b\s*/g, '')
    .trim()

  // First token(s) before `name(` — handles generic types like List<String>
  const m = stripped.match(/^([\w<>\[\]?,\s]+?)\s+\w+\s*\(/)
  if (m) {
    const candidate = m[1].trim().split(/\s+/).pop()
    if (candidate && candidate !== 'static') return candidate
  }

  return 'Object' // constructor or unknown → null default
}

/**
 * Scan backward from errorIdx (0-based) to find the 0-based line index of
 * the innermost enclosing `{`.  Returns -1 if not found.
 */
function findEnclosingBraceOpen(lines, errorIdx) {
  let depth = 0
  for (let i = errorIdx; i >= 0; i--) {
    const line = lines[i]
    for (let j = line.length - 1; j >= 0; j--) {
      const ch = line[j]
      if (ch === '}') depth++
      else if (ch === '{') {
        if (depth === 0) return i
        depth--
      }
    }
  }
  return -1
}

/**
 * Scan forward from openIdx (0-based) to find the matching `}`.
 * Returns -1 if not found.
 */
function findMatchingBraceClose(lines, openIdx) {
  let depth = 0
  for (let i = openIdx; i < lines.length; i++) {
    for (const ch of lines[i]) {
      if (ch === '{') depth++
      else if (ch === '}') {
        depth--
        if (depth === 0) return i
      }
    }
  }
  return -1
}

/**
 * Return true if `line` looks like (part of) a Java method signature.
 */
function looksLikeMethodSig(line) {
  const t = line.trim()
  if (!t || t.startsWith('//') || t.startsWith('*') || t.startsWith('/*')) return false
  // Must have a `(` and either an access modifier or a known return type
  return line.includes('(') && (JAVA_ACCESS_MOD_RE.test(line) || JAVA_METHOD_HINT_RE.test(line))
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Find the enclosing method bounds for a given 1-indexed error line.
 *
 * Returns:
 *   {
 *     startLine: number,      // 1-indexed — method signature start
 *     endLine:   number,      // 1-indexed — closing `}` of method body
 *     signatureLines: string[], // raw lines of the signature (including `{`)
 *     returnType: string,     // inferred return type (may be 'void' or 'Object')
 *   }
 * or null if no method can be reliably detected.
 */
export function findEnclosingMethod(fileContent, errorLine1) {
  const lines = fileContent.split('\n')
  const errorIdx = errorLine1 - 1 // 0-indexed

  // 1. Find the innermost enclosing `{` above the error line
  const openIdx = findEnclosingBraceOpen(lines, errorIdx)
  if (openIdx === -1) return null

  // 2. Scan back from the `{` line (up to 8 lines) for a method signature
  let sigStartIdx = -1
  for (let i = openIdx; i >= Math.max(0, openIdx - 8); i--) {
    const line = lines[i]
    // Stop at class/interface/enum declarations — don't stub the whole class
    if (/^\s*(class|interface|enum|@interface)\s/.test(line)) break
    // Stop at a `}` that isn't on the same line as `{` (left the enclosing scope)
    if (i < openIdx && line.trimStart().startsWith('}')) break

    if (looksLikeMethodSig(line)) {
      sigStartIdx = i
      // Multi-line signature: check if lines above continue the param list
      for (let k = i - 1; k >= Math.max(0, i - 4); k--) {
        const prev = lines[k].trim()
        if (!prev || prev.startsWith('//') || prev.startsWith('*')) break
        if (looksLikeMethodSig(lines[k]) || lines[k].includes('(') || lines[k].includes('@')) {
          sigStartIdx = k
        } else {
          break
        }
      }
      break
    }
  }

  if (sigStartIdx === -1) return null

  // 3. Find the matching closing `}`
  const closeIdx = findMatchingBraceClose(lines, openIdx)
  if (closeIdx === -1) return null

  // 4. Verify the error line is actually inside the method body
  if (errorIdx < openIdx || errorIdx > closeIdx) return null

  const signatureLines = lines.slice(sigStartIdx, openIdx + 1)
  const returnType = extractReturnType(signatureLines)

  console.debug(` [javaStructureParser] method detected: L${sigStartIdx + 1}–${closeIdx + 1} returnType=${returnType}`)

  return {
    startLine: sigStartIdx + 1,
    endLine:   closeIdx + 1,
    signatureLines,
    returnType,
  }
}

/**
 * Build a minimal-compiling stub for a detected method.
 *
 * Preserves the full method signature, replaces the body with:
 *   { /* TODO: <summary> * /   return <default>;  }
 * For void methods the return statement is omitted.
 *
 * @param {ReturnType<findEnclosingMethod>} method
 * @param {string} todoSummary — human-readable reason (from issue.summary)
 * @returns {string} — replacement text for replaceLineRange(startLine, endLine, ...)
 */
export function buildMethodStub(method, todoSummary) {
  const indent      = method.signatureLines[0].match(/^(\s*)/)?.[1] ?? '    '
  const innerIndent = indent + '    '

  // Rebuild signature lines, removing any trailing `{` from the last line
  const cleanedSig = method.signatureLines.map((l, idx) =>
    idx === method.signatureLines.length - 1
      ? l.trimEnd().replace(/\s*\{?\s*$/, '')
      : l.trimEnd()
  )

  const safe = (todoSummary || 'manual resolution required')
    .replace(/\*\//g, '* /')   // prevent accidental comment close
    .slice(0, 200)

  const defaultVal  = defaultForType(method.returnType)
  const returnStmt  = defaultVal !== null ? `\n${innerIndent}return ${defaultVal};` : ''

  return [
    ...cleanedSig,
    `${indent}{`,
    `${innerIndent}/* TODO: ${safe} */${returnStmt}`,
    `${indent}}`,
  ].join('\n')
}
